import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Package, 
  Truck, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  Search,
  Home,
  ShoppingBag
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

/**
 * Public Order Tracking Page
 * - No login required
 * - Token-based tracking via URL parameter
 * - Displays minimal order information for security
 */
const OrderTrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [trackingToken, setTrackingToken] = useState(searchParams.get('token') || '');
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // Auto-search if token is in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setTrackingToken(token);
      fetchOrderTracking(token);
    }
  }, [searchParams]);

  const fetchOrderTracking = async (token) => {
    if (!token || !token.trim()) {
      setError('Please enter a valid tracking code.');
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const response = await fetch(`${API_BASE}/api/public/order-tracking?token=${encodeURIComponent(token)}`);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setError('Too many requests. Please wait a few minutes and try again.');
        } else if (response.status === 404) {
          setError('Order not found. Please check your tracking code.');
        } else {
          setError(data.detail || 'Failed to retrieve order information.');
        }
        setOrderData(null);
        return;
      }

      setOrderData(data);
    } catch (err) {
      console.error('Tracking fetch error:', err);
      setError('Connection error. Please try again.');
      setOrderData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrderTracking(trackingToken);
  };

  // Status icons and colors
  const getStatusConfig = (status) => {
    const configs = {
      created: { 
        icon: Clock, 
        color: 'text-gray-500', 
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-300'
      },
      paid: { 
        icon: Package, 
        color: 'text-green-600', 
        bgColor: 'bg-green-50',
        borderColor: 'border-green-300'
      },
      processing: { 
        icon: Package, 
        color: 'text-yellow-600', 
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-300'
      },
      shipped: { 
        icon: Truck, 
        color: 'text-blue-600', 
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-300'
      },
      delivered: { 
        icon: CheckCircle2, 
        color: 'text-purple-600', 
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-300'
      },
      cancelled: { 
        icon: AlertCircle, 
        color: 'text-red-600', 
        bgColor: 'bg-red-50',
        borderColor: 'border-red-300'
      }
    };
    return configs[status] || configs.created;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
      <div className="container mx-auto max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
            <Package className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Order Tracking
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Track the current status of your order
          </p>
        </div>

        {/* Search Form */}
        <Card className="mb-6 dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <Input
                type="text"
                placeholder="Enter your tracking code..."
                value={trackingToken}
                onChange={(e) => setTrackingToken(e.target.value)}
                className="flex-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                data-testid="tracking-token-input"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="tracking-search-btn"
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-700 dark:text-red-400">
                <AlertCircle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Order Data */}
        {orderData && (
          <Card className="dark:bg-gray-800 dark:border-gray-700" data-testid="order-tracking-result">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="dark:text-white">
                  Order #{orderData.order_reference}
                </CardTitle>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusConfig(orderData.status).bgColor} ${getStatusConfig(orderData.status).color}`}>
                  {orderData.status_label}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className={`p-4 rounded-lg border ${getStatusConfig(orderData.status).bgColor} ${getStatusConfig(orderData.status).borderColor}`}>
                <div className="flex items-center gap-3">
                  {React.createElement(getStatusConfig(orderData.status).icon, {
                    className: `w-6 h-6 ${getStatusConfig(orderData.status).color}`
                  })}
                  <div>
                    <p className={`font-semibold ${getStatusConfig(orderData.status).color}`}>
                      {orderData.status_label}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {orderData.status_description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tracking Number (if available) */}
              {orderData.tracking_number && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-400">
                    <strong>Carrier Tracking Number:</strong> {orderData.tracking_number}
                  </p>
                </div>
              )}

              {/* Timeline */}
              {orderData.timeline && orderData.timeline.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Order History
                  </h3>
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
                    
                    {/* Timeline events */}
                    <div className="space-y-4">
                      {orderData.timeline.map((event, index) => {
                        const config = getStatusConfig(event.status);
                        return (
                          <div key={`${event.status}-${index}`} className="flex gap-4 relative">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center z-10 ${event.completed ? config.bgColor : 'bg-gray-100 dark:bg-gray-700'}`}>
                              {React.createElement(config.icon, {
                                className: `w-3 h-3 ${event.completed ? config.color : 'text-gray-400'}`
                              })}
                            </div>
                            <div className="flex-1 pb-4">
                              <p className={`font-medium ${event.completed ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                                {event.label}
                              </p>
                              {event.timestamp && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatDate(event.timestamp)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Last Updated */}
              {orderData.last_updated && (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center pt-4 border-t dark:border-gray-700">
                  Last updated: {formatDate(orderData.last_updated)}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!orderData && searched && !error && !loading && (
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                Order information not found
              </p>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-center gap-4 mt-8">
          <Link to="/">
            <Button variant="outline" className="dark:border-gray-600 dark:text-gray-300">
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </Link>
          <Link to="/shop">
            <Button variant="outline" className="dark:border-gray-600 dark:text-gray-300">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Shop
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
